/**
 * components/fasteners/Fastener.tsx — MUTA parametric fastener (PLAN §2.18, T14).
 *
 * PRESENTATION component: receives a `FastenerAnchor` ALREADY resolved by
 * `model/`(world position, axis = hole.normal transformed, diameter,
 * length) and renders a parametric fastener:
 *
 * - HEAD: short hexagonal prism (`headSegments: 6`), at the inlet end (anchor)
 * - SHANK: gambo "filettato" procedurale (`threadedShankGeometry.ts` — anelli
 * radice/cresta rivoluzionati, NON un'elica vera, vedi doc-comment li'),
 * dall'ancoraggio lungo l'asse di inserimento, terminato da una punta conica
 * (28-09-2026, richiesta utente: "migliora il modello, non perfetto")
 *
 * Orientation is derived ONLY from `anchor.axis`: the quaternion carries the
 * local Y (0,1,0) on the fastener axis (setFromUnitVectors). No calculation of
 * positions outside of this (the THREE layer is the render).
 *
 * R6 (GLB): il backend/l'admin supportano gia' un GLB per fastener
 * (`anchor.glbUrl`, `fastener_3d` — vedi `hooks/useAssets.ts`), ma l'utente
 * ha scelto esplicitamente (28-09-2026) di NON caricare asset reali: questo
 * componente resta quindi SOLO procedurale, il branch GLB (TODO storico) non
 * viene implementato per ora.*/

import { Line } from "@react-three/drei";
import { useEffect, useMemo } from "react";
import * as THREE from "three";
import { COLORS } from "../../config/colors";
import { FASTENER_FALLBACK_DEFAULTS } from "../../config/defaults";
import type { Vec3 } from "../../data/types";
import type { FastenerAnchor } from "../../model/types";
import { clamp } from "../../utils/math";
import { buildThreadedShankGeometry } from "./threadedShankGeometry";

// ─── Props (PLAN §2.18) ───────────────────────────────────────

export interface FastenerProps {
  /** Still lives ALREADY solved (position + axis + dims).*/
  anchor: FastenerAnchor;
  /** Position override (render layer: position + Δshift STEP 6 + explode).*/
  position?: Vec3;
  /** Fastener color (from config/colors.ts, chosen by holeType in T15).*/
  color: string;
  /** Show debug helper (axis line).*/
  showDebug?: boolean;
  /** Material opacity (0..1) — overlay componentsState.*/
  opacity?: number;
  /** Group visibility (overlay componentsState).*/
  visible?: boolean;
}

//─── Component ─────────────────────── ────────────────────────

/**
 * Renders the parametric fastener at the anchor, oriented along the axis.
 * The head is at the entry point (anchor.position), the shank continues
 * long `anchor.axis` by `anchor.lengthMm`.*/
export function Fastener({ anchor, position, color, showDebug = false, opacity = 1, visible = true }: FastenerProps) {
  //Quaternion that brings the local Y (0,1,0) on the fastener axis (render layer).
  const quaternion = useMemo(() => {
    const axis = new THREE.Vector3(anchor.axis[0], anchor.axis[1], anchor.axis[2]).normalize();
    // If the axis is (nearly) zero or already parallel to Y, setFromUnitVectors is safe;
    //for the degenerate case (0,0,0) we use identity.
    if (axis.lengthSq() < 1e-12) return new THREE.Quaternion();
    return new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), axis);
  }, [anchor.axis]);

  // fix P8: clamp demandato alla utility pura `utils/math.clamp` (unilaterale,
  //max = +Infinity as documented: same behavior as Math.max).
  const diameter = clamp(anchor.diameterMm, 1, Number.POSITIVE_INFINITY);
  const headHeightMm = FASTENER_FALLBACK_DEFAULTS.headHeightMm;
  const length = clamp(anchor.lengthMm, headHeightMm, Number.POSITIVE_INFINITY);
  const shankLength = length - headHeightMm;
  const headRadius = (diameter * FASTENER_FALLBACK_DEFAULTS.headDiameterFactor) / 2;
  const shankRadius = diameter / 2;

  //Geometria procedurale del gambo filettato (threadedShankGeometry.ts) —
  //NON gestita da JSX (<cylinderGeometry/> e' dichiarativa e auto-smaltita da
  //R3F), quindi va smaltita a mano come le altre geometrie imperative del
  //modulo (stesso pattern di GlbView.tsx per gli spigoli).
  const threadGeometry = useMemo(
    () =>
      buildThreadedShankGeometry({
        radiusMm: shankRadius,
        lengthMm: shankLength,
        pitchMm: diameter * FASTENER_FALLBACK_DEFAULTS.threadPitchFactor,
        threadDepthFactor: FASTENER_FALLBACK_DEFAULTS.threadDepthFactor,
        tipLengthMm: diameter * FASTENER_FALLBACK_DEFAULTS.tipLengthFactor,
        segments: FASTENER_FALLBACK_DEFAULTS.shankSegments,
      }),
    [shankRadius, shankLength, diameter],
  );
  useEffect(() => () => threadGeometry.dispose(), [threadGeometry]);

  const pos = position ?? anchor.position;

  //The group is centered at the anchor; the local Y axis points along axis.
  return (
    <group position={pos} quaternion={quaternion} visible={visible}>
      {/*Head: prisma esagonale (headSegments: 6, "aspetto bullone" invece del
      cilindro liscio) all'estremita' di inserimento (y = 0..headHeight).*/}
      <mesh position={[0, headHeightMm / 2, 0]} castShadow>
        <cylinderGeometry
          args={[headRadius, headRadius, headHeightMm, FASTENER_FALLBACK_DEFAULTS.headSegments]}
        />
        <meshStandardMaterial
          color={color}
          roughness={0.4}
          metalness={0.7}
          transparent={opacity < 1}
          opacity={opacity}
        />
      </mesh>

      {/* Shank: gambo filettato procedurale (anelli radice/cresta + punta
      conica), dall'ancoraggio (y=headHeightMm, NON centrato — la geometria
      parte gia' da y=0 al proprio attacco) lungo l'asse. */}
      <mesh position={[0, headHeightMm, 0]} geometry={threadGeometry} castShadow>
        <meshStandardMaterial
          color={color}
          roughness={0.5}
          metalness={0.6}
          transparent={opacity < 1}
          opacity={opacity}
        />
      </mesh>

      {/*Fastener axis in debug: lineAux along the axis from the anchor.*/}
      {showDebug ? <AxisDebug length={length} /> : null}
    </group>
  );
}

//─── Internal sub-component ─────────────────────────────────

/**
 * AxisDebug — line along the local Y axis (which coincides with anchor.axis
 * thanks to the group quaternion) to check the fastener orientation.
 * Use drei's `Line` (the JSX `<line>` collides with the SVG element in r3f).*/
function AxisDebug({ length }: { length: number }) {
  const points = useMemo<[THREE.Vector3, THREE.Vector3]>(
    () => [new THREE.Vector3(0, 0, 0), new THREE.Vector3(0, length, 0)],
    [length],
  );
  return <Line points={points} color={COLORS.fastenerAxis} />;
}
